import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { 
  Users, Plus, Search, ChevronLeft, ChevronRight, 
  Eye, Edit2, Trash2, AlertCircle, X, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import debounce from 'lodash/debounce';

const normalizeRole = (role) => {
  if (!role) return '';
  const r = String(role).trim().toLowerCase();
  if (r === 'admin' || r === 'system_admin' || r === 'system-admin' || r === 'system administrator') return 'admin';
  return r;
};

const conditionBadges = {
  diabetes: 'bg-brand-red-50 text-brand-red-700 border-brand-red-200',
  hypertension: 'bg-brand-orange-50 text-brand-orange-700 border-brand-orange-200',
  obesity: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  gut_health: 'bg-brand-purple-50 text-brand-purple-700 border-brand-purple-200',
  food_allergies: 'bg-pink-50 text-pink-700 border-pink-200',
  sports_nutrition: 'bg-brand-blue-50 text-brand-blue-700 border-brand-blue-200',
  maternal_child: 'bg-brand-green-50 text-brand-green-700 border-brand-green-200',
  weight_management: 'bg-indigo-50 text-indigo-700 border-indigo-200'
};

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    whatsapp: '',
    date_of_birth: '',
    gender: '',
    address: '',
    nutrition_goals: [],
    conditions: [],
    allergies: [],
    notes: ''
  });

  const nutritionGoalsOptions = [
    'Weight Loss',
    'Weight Gain',
    'Muscle Building',
    'General Wellness',
    'Disease Management',
    'Sports Performance',
    'Detoxification',
    'Digestive Health'
  ];

  const conditionOptions = [
    'diabetes',
    'hypertension',
    'obesity',
    'gut_health',
    'food_allergies',
    'sports_nutrition',
    'maternal_child',
    'weight_management'
  ];

  const fetchClients = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const response = await api.get('/clients', {
        params: { search }
      });
      const data = response.data;
      // API returns an array of clients directly
      if (Array.isArray(data)) {
        setClients(data);
      } else if (data.clients) {
        setClients(data.clients);
      } else {
        setClients([]);
      }
      setTotalPages(1);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Failed to load clients');
      setClients([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchClients(searchTerm);
  }, [fetchClients, searchTerm]);

  const debouncedSearch = useCallback(
    debounce((search) => {
      setCurrentPage(1);
      fetchClients(search);
    }, 500),
    [fetchClients]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGoalToggle = (goal) => {
    setFormData(prev => ({
      ...prev,
      nutrition_goals: prev.nutrition_goals.includes(goal)
        ? prev.nutrition_goals.filter(g => g !== goal)
        : [...prev.nutrition_goals, goal]
    }));
  };

  const handleConditionToggle = (condition) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.includes(condition)
        ? prev.conditions.filter(c => c !== condition)
        : [...prev.conditions, condition]
    }));
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      whatsapp: '',
      date_of_birth: '',
      gender: '',
      address: '',
      nutrition_goals: [],
      conditions: [],
      allergies: [],
      notes: ''
    });
    setEditingClient(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (client) => {
    setFormData({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      whatsapp: client.whatsapp || '',
      date_of_birth: client.date_of_birth ? client.date_of_birth.split('T')[0] : '',
      gender: client.gender || '',
      address: client.address || '',
      nutrition_goals: client.nutrition_goals || [],
      conditions: client.conditions || [],
      allergies: client.allergies || [],
      notes: client.notes || ''
    });
    setEditingClient(client);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        allergies: formData.allergies ? formData.allergies.split(',').map(a => a.trim()).filter(Boolean) : []
      };

      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, payload);
        toast.success('Client updated successfully');
      } else {
        await api.post('/clients', payload);
        toast.success('Client created successfully');
      }

      setShowAddModal(false);
      resetForm();
      fetchClients(searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save client');
    }
    setSubmitting(false);
  };

  const handleDeleteClient = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client deleted successfully');
      setShowDeleteConfirm(null);
      fetchClients(searchTerm);
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const colors = [
    'bg-brand-green', 'bg-brand-blue', 'bg-brand-purple',
    'bg-brand-orange', 'bg-pink-500', 'bg-teal-500',
    'bg-indigo-500', 'bg-brand-red'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-brand-blue-100 rounded-lg">
            <Users size={24} className="text-brand-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-dark">Clients</h1>
            <p className="text-sm text-neutral-gray">Manage your client records</p>
          </div>
        </div>

        <button
          onClick={openAddModal}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>Add Client</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-gray" />
        <input
          type="text"
          placeholder="Search clients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-green"></div>
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12">
          <Users size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-neutral-gray font-medium">No clients found</p>
          <p className="text-sm text-neutral-gray mt-1">Add your first client to get started</p>
        </div>
      ) : (
        <>
          {/* Client Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client) => {
              const initial = (client.first_name?.[0] || client.last_name?.[0] || '?').toUpperCase();
              const colorIndex = client.id % colors.length;

              return (
                <div key={client.id} className="bg-white rounded-xl shadow-sm border border-neutral-medium p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg ${colors[colorIndex]}`}>
                        {initial}
                      </div>
                      <div>
                        <p className="font-semibold text-neutral-dark">
                          {client.first_name} {client.last_name}
                        </p>
                        <p className="text-xs text-neutral-gray">{client.email || 'No email'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-neutral-gray">
                      <span className="font-medium">Phone:</span> {client.phone || 'N/A'}
                    </p>
                    {client.nutrition_goals?.length > 0 ? (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium badge-success">
                        {client.nutrition_goals[0].replace(/_/g, ' ')}
                      </span>
                    ) : null}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-neutral-medium">
                    <button
                      onClick={() => navigate(`/clients/${client.id}`)}
                      className="p-2 text-neutral-gray hover:text-brand-blue hover:bg-brand-blue-50 rounded-lg transition-colors"
                      title="View client"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => openEditModal(client)}
                      className="p-2 text-neutral-gray hover:text-brand-green hover:bg-brand-green-50 rounded-lg transition-colors"
                      title="Edit client"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(client)}
                      className="p-2 text-neutral-gray hover:text-brand-red hover:bg-brand-red-50 rounded-lg transition-colors"
                      title="Delete client"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-6">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-neutral-medium hover:bg-neutral-light disabled:opacity-50"
              >
                <ChevronLeft size={16} />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === pageNum
                      ? 'bg-brand-green text-white'
                      : 'bg-white border border-neutral-medium text-neutral-gray hover:bg-neutral-light'
                  }`}
                >
                  {pageNum}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-neutral-medium hover:bg-neutral-light disabled:opacity-50"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-neutral-medium px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-semibold text-neutral-dark">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h2>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    First Name <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Last Name <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">WhatsApp</label>
                  <input
                    type="text"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Date of Birth</label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nutrition Goals</label>
                <div className="flex flex-wrap gap-2">
                  {nutritionGoalsOptions.map(goal => (
                    <button
                      key={goal}
                      type="button"
                      onClick={() => handleGoalToggle(goal)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        formData.nutrition_goals.includes(goal)
                          ? 'bg-brand-green text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {goal}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Medical Conditions</label>
                <div className="flex flex-wrap gap-2">
                  {conditionOptions.map(condition => (
                    <button
                      key={condition}
                      type="button"
                      onClick={() => handleConditionToggle(condition)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        formData.conditions.includes(condition)
                          ? 'bg-brand-red text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {condition.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Allergies (comma separated)
                </label>
                <input
                  type="text"
                  name="allergies"
                  value={formData.allergies}
                  onChange={(e) => setFormData(prev => ({ ...prev, allergies: e.target.value }))}
                  placeholder="e.g. peanuts, shellfish, pollen"
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-medium">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-5 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-50"
                >
                  {submitting ? (
                    <span className="flex items-center space-x-2">
                      <Loader2 size={16} className="animate-spin" />
                      <span>Saving...</span>
                    </span>
                  ) : (
                    editingClient ? 'Update Client' : 'Add Client'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-brand-red-100 rounded-full">
                <AlertCircle size={24} className="text-brand-red" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-dark">Delete Client</h3>
                <p className="text-sm text-neutral-gray">
                  Are you sure you want to delete {showDeleteConfirm.first_name} {showDeleteConfirm.last_name}?
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteClient(showDeleteConfirm.id)}
                className="px-4 py-2.5 bg-brand-red text-white rounded-xl text-sm font-medium hover:bg-brand-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;